import React, { useState, useEffect } from 'react';
import { post } from '../services/http-service';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import storageService from 'src/services/storage-service';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import { toast } from 'react-toastify';
import DialogClose from './DialogClose';

const FIELDS = [
	{ label: 'Ditt meddelande', required: true, multiline: true, rows: 4 },
	{ label: 'E-postadress', required: true, autoComplete: 'email' },
	{ label: 'Namn', required: true, autoComplete: 'name' },
	{
		label: 'Företag/Organisation',
		required: false,
		autoComplete: 'organization',
	},
	{ label: 'Avdelning/Förvaltning', required: false },
	{ label: 'Telefon', required: false, autoComplete: 'tel' },
];

const OPTIONS = [
	'Support IT',
	'Beställ dom',
	'Juridisk rådgivning',
	'Prisuppgift',
	'Förbättringsförslag',
	'Glömt gemensamt lösenord',
	'Övrigt',
];

function ContactUsDialog({ open, onClose, initalOption, initialMessage }) {
	const [option, setOption] = useState('Support IT');
	const [errorMessage, setErrorMessage] = useState('');
	const [fields, setFields] = useState([]);

	useEffect(() => {
		const user = storageService.load();

		// Create a fresh copy of the fields from the template
		const updatedFields = FIELDS.map((field, index) => {
			const newField = { ...field }; // shallow copy
			switch (index) {
				case 0:
					newField.value = initialMessage || '';
					break;
				case 1:
					newField.value =
						user.email && user.email.indexOf('@') > 0
							? user.email
							: '';
					break;
				case 2:
					newField.value = user.displayName || '';
					break;
				case 3:
					newField.value = user.organizationName || '';
					break;
				default:
					newField.value = newField.value || '';
			}
			return newField;
		});

		// If a certain option is selected, adjust required fields accordingly
		if (initalOption === 'Glömt gemensamt lösenord') {
			updatedFields[3].required = true;
			updatedFields[4].required = true;
		}

		setOption(initalOption);
		setErrorMessage('');
		setFields(updatedFields);
	}, [initalOption, initialMessage]);

	const changeField = (index, value) => {
		const newFields = [...fields];
		newFields[index].value = value;
		setFields(newFields);
	};

	const onSubmit = async () => {
		// Check required fields from the 'fields' state, not FIELDS
		for (let i = 0; i < fields.length; i++) {
			if (fields[i].required && !fields[i].value) {
				return setErrorMessage(`${fields[i].label} är obligatoriskt`);
			}
		}

		const form = new FormData();
		form.append('subject', option);
		form.append('message', fields[0]?.value || '');
		form.append('email', fields[1]?.value || '');
		form.append('name', fields[2]?.value || '');
		form.append('company', fields[3]?.value || '');
		form.append('org', fields[4]?.value || '');
		form.append('phone', fields[5]?.value || '');

		try {
			await post('/contact', form, true);
			setErrorMessage('');
			toast.success('Ditt meddelande har skickats');
			onClose(true);
		} catch (error) {
			// handle error if needed
			setErrorMessage('Ett fel uppstod vid skickning av meddelandet');
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => onClose(true)}
			scroll="body"
			aria-labelledby="scroll-dialog-title"
			aria-describedby="scroll-dialog-description"
		>
			<Box minWidth={600}>
				<DialogTitle id="scroll-dialog-title">Kontakta Oss</DialogTitle>
				<DialogClose onClose={() => onClose(true)} />

				<DialogContent dividers>
					{errorMessage && (
						<Box mb={4}>
							<Alert variant="filled" severity="error">
								{errorMessage}
							</Alert>
						</Box>
					)}

					<FormGroup>
						<FormControl size="small" margin="dense" fullWidth>
							<InputLabel>Vad gäller ditt ärende</InputLabel>
							<Select
								value={option}
								label="Vad gäller ditt ärende"
								onChange={(event) =>
									setOption(event.target.value)
								}
							>
								{OPTIONS.map((option, index) => (
									<MenuItem key={index} value={option}>
										{option}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						{option === 'Juridisk rådgivning' && (
							<Typography variant="body2" color="text.secondary">
								Infosoc juridiska rådgivning omfattar rätten att
								ställa frågor och diskutera ärenden med våra
								jurister. Detta innebär att vi tittar på er
								fråga i upp till 30 minuter och ger er
								vägledning kring densamma utifrån vår databas
								innehåll. I praktiken innebär det att juristen
								tittar på tidigare praxis gällande
								frågeställningen eller om vägledning återfinns i
								t.ex. förarbeten och lagtext. Normal svarstid
								1-2 arbetsdagar.
							</Typography>
						)}

						{option === 'Glömt gemensamt lösenord' && (
							<Typography variant="body2" color="text.secondary">
								Ange dina kontaktuppgifter i formuläret så
								kontaktar en utav våra representanter dig
								snarast möjligt.
							</Typography>
						)}

						{fields.map((field, index) => (
							<TextField
								key={index}
								onChange={(e) =>
									changeField(index, e.target.value)
								}
								value={field.value}
								multiline={field.multiline}
								rows={field.rows || 1}
								margin="normal"
								required={field.required}
								fullWidth
								label={field.label}
								autoComplete={field.autoComplete || undefined}
							/>
						))}
					</FormGroup>
				</DialogContent>
			</Box>
			<DialogActions>
				<Button variant="contained" onClick={onSubmit}>
					Skicka
				</Button>
				<Button onClick={() => onClose(false)}>Avbryt</Button>
			</DialogActions>
		</Dialog>
	);
}

ContactUsDialog.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func,
	initalOption: PropTypes.string,
	initialMessage: PropTypes.string,
};

export default ContactUsDialog;
